import { useEffect, useRef, useState, type ReactNode } from 'react'

/** Общие детали опытов «Лаборатории»: ползунок, выбор, вопрос «предскажи», стрелки, цикл кадров. */

/** Ползунок с подписью и значением. */
export function Slider(props: {
  label: string
  value: number
  min: number
  max: number
  step: number
  unit: string
  shown?: string
  onChange: (v: number) => void
}) {
  return (
    <label className="lab-slider">
      <span>
        {props.label}: <b>{props.shown ?? props.value} {props.unit}</b>
      </span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
      />
    </label>
  )
}

/** Ряд кнопок-переключателей: выбрана одна. */
export function Choice<T extends string | number>(props: {
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="row-wrap lab-choice">
      {props.options.map((o) => (
        <button
          key={String(o.value)}
          className={o.value === props.value ? 'btn btn-primary' : 'btn'}
          onClick={() => props.onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Вопрос «предскажи»: ученик выбирает ответ до опыта. Ответ показывается, когда
 * опыт его проверил (revealed), — так закономерность становится «своей».
 */
export function Predict(props: {
  question: string
  options: readonly string[]
  right: number
  revealed: boolean
  explain: ReactNode
  /** Сменился опыт — прежний ответ снимается. */
  resetKey?: string
}) {
  const [выбор, setВыбор] = useState<number | null>(null)
  useEffect(() => setВыбор(null), [props.resetKey])
  return (
    <div className="lab-predict">
      <div className="lab-predict-q">🤔 {props.question}</div>
      <div className="row-wrap">
        {props.options.map((o, i) => (
          <button
            key={o}
            className={выбор === i ? 'btn btn-primary' : 'btn'}
            onClick={() => setВыбор(i)}
          >
            {o}
          </button>
        ))}
      </div>
      {props.revealed && выбор !== null && (
        <div className={выбор === props.right ? 'lab-verdict ok' : 'lab-verdict'}>
          {выбор === props.right ? '✅ Верно. ' : `Ответ: ${props.options[props.right]}. `}
          {props.explain}
        </div>
      )}
      {!props.revealed && выбор !== null && <div className="muted small">Теперь проверь опытом.</div>}
    </div>
  )
}

/** Наконечники стрелок — по одному на цвет; кладутся в <defs> каждого рисунка. */
export function ArrowDefs() {
  return (
    <defs>
      {(['heavy', 'el', 'ink', 'band'] as const).map((k) => (
        <marker key={k} id={`head-${k}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" className={`fill-${k}`} />
        </marker>
      ))}
    </defs>
  )
}

/** Стрелка силы: короче 4 px не рисуется (сила почти ноль). */
export function Arrow(props: {
  x1: number
  y1: number
  x2: number
  y2: number
  kind: 'heavy' | 'el' | 'ink' | 'band'
  label?: string
  labelDx?: number
  labelDy?: number
}) {
  const len = Math.hypot(props.x2 - props.x1, props.y2 - props.y1)
  if (len < 4) return null
  return (
    <>
      <line
        x1={props.x1} y1={props.y1} x2={props.x2} y2={props.y2}
        className={`ink-${props.kind}`} strokeWidth={3} markerEnd={`url(#head-${props.kind})`}
      />
      {props.label && (
        <text
          x={props.x2 + (props.labelDx ?? 6)} y={props.y2 + (props.labelDy ?? 4)}
          className={`lab-label ${props.kind}`}
        >
          {props.label}
        </text>
      )}
    </>
  )
}

/** Цикл анимации: шаг симуляции на каждый кадр браузера. */
export function useFrames(step: (dtSec: number) => void) {
  const stepRef = useRef(step)
  stepRef.current = step
  useEffect(() => {
    let id = 0
    let prev = performance.now()
    const tick = (now: number) => {
      // шаг не больше 50 мс (вкладка уходила в фон) и не отрицательный
      stepRef.current(Math.min(Math.max((now - prev) / 1000, 0), 0.05))
      prev = now
      id = requestAnimationFrame(tick)
    }
    id = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(id)
  }, [])
}

/** Пружина-зигзаг от (x, y) вниз на длину len. */
export function зигзаг(x: number, y: number, len: number, витков = 8, ширина = 7): string {
  const точки = [`${x},${y}`]
  for (let i = 1; i < витков * 2; i++) точки.push(`${x + (i % 2 ? ширина : -ширина)},${y + (len * i) / (витков * 2)}`)
  точки.push(`${x},${y + len}`)
  return точки.join(' ')
}
